import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy - Wild Thyme';
  }, []);

  return (
    <div className="min-h-screen pt-32" data-testid="privacy-policy-page">
      <section className="py-16 md:py-24 bg-[#F9F8F6]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[#D4A373] hover:underline mb-8"
              data-testid="back-home"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl leading-tight text-[#2C332B] mb-4">
              Privacy Policy
            </h1>
            <p className="text-[#6C757D]">Last updated: February 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 prose-policy">
          <div className="space-y-10 text-[#6C757D] leading-relaxed">
            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">1. Who We Are</h2>
              <p>
                Wild Thyme ("we", "us", "our") is a women's wellbeing retreat company
                based in the United Kingdom. We are committed to protecting your personal
                data and respecting your privacy in accordance with the UK General Data
                Protection Regulation (UK GDPR) and the Data Protection Act 2018.
              </p>
              <p className="mt-3">
                For any privacy-related enquiries, please contact us at{' '}
                <a href="mailto:team@wild-thyme.co.uk" className="text-[#D4A373] hover:underline">
                  team@wild-thyme.co.uk
                </a>.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">2. What Data We Collect</h2>
              <p>We may collect the following personal data:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Name and contact details (when you enquire or book a retreat)</li>
                <li>Email address (when you contact us or join a waiting list)</li>
                <li>Dietary and health information (when you complete a pre-retreat form)</li>
                <li>Website usage data (via analytics cookies, with your consent)</li>
                <li>Cookie preference data (stored locally on your device)</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">3. How We Use Your Data</h2>
              <p>We use your personal data to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Process retreat bookings and enquiries</li>
                <li>Ensure your safety and comfort during retreats</li>
                <li>Respond to your questions and requests</li>
                <li>Improve our website and services (with your consent)</li>
                <li>Send you information about future retreats (only with your explicit consent)</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">4. Legal Basis for Processing</h2>
              <p>We process your data based on:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong>Consent</strong> – for analytics cookies and marketing communications</li>
                <li><strong>Contract</strong> – to fulfil retreat bookings and services</li>
                <li><strong>Legitimate interest</strong> – to respond to enquiries and improve our services</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">5. Cookies</h2>
              <p>
                We use cookies on our website. For detailed information about the cookies
                we use and how to manage them, please see our{' '}
                <Link to="/cookie-policy" className="text-[#D4A373] hover:underline">
                  Cookie Policy
                </Link>.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">6. Data Sharing</h2>
              <p>
                We do not sell your personal data. We may share your data with trusted
                third parties only where necessary to deliver our services (e.g. accommodation
                providers, payment processors). All third parties are required to respect the
                security of your data and treat it in accordance with the law.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">7. Data Retention</h2>
              <p>
                We retain your personal data only for as long as necessary to fulfil the
                purposes for which it was collected. Booking data is retained for up to 6
                years for legal and accounting purposes. Marketing consent records are
                retained until you withdraw consent.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">8. Your Rights</h2>
              <p>Under UK GDPR, you have the right to:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to or restrict processing of your data</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, please contact us at{' '}
                <a href="mailto:team@wild-thyme.co.uk" className="text-[#D4A373] hover:underline">
                  team@wild-thyme.co.uk
                </a>.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-[#2C332B] mb-4">9. Complaints</h2>
              <p>
                If you are unhappy with how we handle your data, you have the right to
                lodge a complaint with the Information Commissioner's Office (ICO) at{' '}
                <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#D4A373] hover:underline">
                  ico.org.uk
                </a>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
